import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'HindukushSoft Virtual Internship Program',
  description = 'Join HindukushSoft\'s comprehensive virtual internship program. Gain hands-on experience in Web Development, AI/ML, and Digital Marketing. 8-week structured learning with industry experts.',
  keywords = 'internship, virtual internship, web development, full stack development, digital marketing, AI, machine learning, WordPress, HindukushSoft, Pakistan internship, remote internship',
  image = 'https://internship.hindukushsoft.com/og-image.png',
  url = 'https://internship.hindukushsoft.com',
  type = 'website',
  trackName = null,
  taskDay = null,
}) => {
  // Dynamic title based on context
  let pageTitle = title;
  if (trackName && taskDay) {
    pageTitle = `Day ${taskDay} - ${trackName} | HindukushSoft Internship`;
  } else if (trackName) {
    pageTitle = `${trackName} Track | HindukushSoft Internship`;
  }

  // Dynamic description based on context
  let pageDescription = description;
  if (trackName) {
    const trackDescriptions = {
      'WordPress Development': 'Master WordPress development, themes, plugins, and WooCommerce. Build professional websites and web applications.',
      'Full-Stack Development': 'Learn HTML, CSS, JavaScript, React, Laravel, and MySQL. Become a complete full-stack developer.',
      'Artificial Intelligence & Machine Learning': 'Explore Machine Learning, Deep Learning, Neural Networks, and AI applications with Python.',
      'Digital Marketing': 'Learn SEO, social media marketing, content marketing, email marketing, and analytics.',
    };
    pageDescription = trackDescriptions[trackName] || description;
  }

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'HindukushSoft Virtual Internship Program',
    description: 'Professional virtual internship program offering hands-on training in technology and digital skills.',
    url: 'https://internship.hindukushsoft.com',
    logo: 'https://internship.hindukushsoft.com/logo.png',
    sameAs: [
      'https://hindukushsoft.com',
      'https://www.linkedin.com/company/hindukushsoft',
      'https://www.facebook.com/hindukushsoft',
    ],
    offers: {
      '@type': 'Offer',
      category: 'Virtual Internship',
      priceCurrency: 'PKR',
      price: '0',
      availability: 'https://schema.org/InStock',
    },
    provider: {
      '@type': 'Organization',
      name: 'HindukushSoft',
      url: 'https://hindukushsoft.com',
    },
  };

  // Course structured data for tracks
  const courseStructuredData = trackName ? {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: trackName,
    description: pageDescription,
    provider: {
      '@type': 'Organization',
      name: 'HindukushSoft',
      url: 'https://hindukushsoft.com',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      duration: 'P8W',
    },
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="HindukushSoft" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="HindukushSoft Internship" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@hindukushsoft" />
      <meta name="twitter:creator" content="@hindukushsoft" />
      
      {/* Additional SEO */}
      <meta name="geo.region" content="PK" />
      <meta name="geo.placename" content="Pakistan" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Mobile Optimization */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="HindukushSoft Internship" />
      
      {/* Theme Colors */}
      <meta name="theme-color" content="#6634F1" />
      <meta name="msapplication-TileColor" content="#6634F1" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {courseStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(courseStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
